// 📦 components/AddToCartButton.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useCart } from "../context/CartContext";
import { SHOPIFY_STORE_DOMAIN } from "../utils/shopify";

const AddToCartButton = ({
  variantId,
  available = true,
  quantity = 1,
  children = "Add to Cart",
  checkoutLabel = "Go to Cart/Checkout",
  successBehavior = "secondary",
  showContinueShoppingLink = false,
  onAdded,
  layoutClassName = "",
  buttonClassName = "",
  checkoutClassName = "",
}) => {
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const { addToCart } = useCart();

  const fallbackCartUrl = useMemo(
    () => `https://${SHOPIFY_STORE_DOMAIN}/cart`,
    []
  );

  useEffect(() => {
    setAdded(false);
    setCheckoutUrl("");
  }, [variantId, quantity]);

  const resetToDefault = () => {
    setAdded(false);
    setCheckoutUrl("");
  };

  const handleAdd = async () => {
    if (!variantId || !available) return;
    setBusy(true);
    
    try {
      // ✅ Add to cart via CartContext (automatically updates state)
      const updatedCart = await addToCart(variantId, quantity);
      console.log("✅ Item added to cart:", updatedCart);

      // Keep a direct path into the active Shopify cart or checkout.
      setCheckoutUrl(updatedCart?.checkoutUrl || fallbackCartUrl);
      setAdded(true);
      onAdded?.(updatedCart);
    } catch (err) {
      console.error("❌ Failed to add to cart:", err);
      alert("Failed to add item to cart. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const baseButtonClasses =
    "px-4 py-2 rounded font-bold text-white transition-all duration-200";
  const addButtonStateClasses =
    busy || !available
      ? "bg-gray-400 cursor-not-allowed"
      : "bg-brandred hover:bg-red-600";
  const replaceButtonStateClasses = busy
    ? "bg-brandred opacity-80 cursor-wait"
    : !available
    ? "bg-gray-400 cursor-not-allowed"
    : "bg-brandred hover:bg-red-700";

  return (
    <div className={`flex flex-col gap-2 ${layoutClassName}`.trim()}>
      {successBehavior === "replace" && added && checkoutUrl ? (
        <>
          <a
            href={checkoutUrl}
            className={`${baseButtonClasses} ${replaceButtonStateClasses} text-center ${buttonClassName} ${checkoutClassName}`.trim()}
          >
            {checkoutLabel}
          </a>

          {showContinueShoppingLink && (
            <p className="text-center text-xs text-gray-300 transition-opacity duration-200">
              Item added ·{" "}
              <button
                type="button"
                onClick={resetToDefault}
                className="text-brandred underline underline-offset-2 hover:text-red-300"
              >
                continue shopping
              </button>
            </p>
          )}
        </>
      ) : (
        <>
          <button
            type="button"
            disabled={busy || !available}
            onClick={handleAdd}
            className={`${baseButtonClasses} ${
              successBehavior === "replace"
                ? replaceButtonStateClasses
                : addButtonStateClasses
            } ${buttonClassName}`.trim()}
          >
            {busy ? "Adding..." : available ? children : "Sold Out"}
          </button>

          {successBehavior === "secondary" && added && checkoutUrl && (
            <a
              href={checkoutUrl}
              className={`px-4 py-2 rounded font-bold text-center text-brandred border border-brandred transition hover:bg-brandred hover:text-white ${checkoutClassName}`.trim()}
            >
              {checkoutLabel}
            </a>
          )}
        </>
      )}
    </div>
  );
};

export default AddToCartButton;
